/** "The Evening" section — title, two event photos, and short description. */
import { ScrollReveal } from './components/ScrollReveal';
import { HDEDesktopFrame } from './components/HDEDesktopFrame';

interface TheEveningProps {
    assets: { photo1: string; photo2: string };
    isMobile?: boolean;
    onRegister?: () => void;
    showRegisterButton?: boolean;
}

export const TheEvening = ({
    assets,
    isMobile = false,
    onRegister,
    showRegisterButton = true,
}: TheEveningProps) => {
    if (isMobile) {
        return (
            <section
                data-testid="the-evening"
                className="relative mb-[3.65rem] w-full bg-white px-[1.5rem] pt-[1.75rem]"
            >
                <ScrollReveal animation="fade-left" duration={350}>
                    <p className="font-sans text-[1.25rem] leading-none font-medium text-black uppercase">
                        The Evening
                    </p>
                </ScrollReveal>
                <div className="mt-[1.25rem] grid grid-cols-2 gap-[0.375rem]">
                    <img
                        src={assets.photo1}
                        alt="Home Design Evening event"
                        className="h-[14rem] w-full object-cover object-[58%_center]"
                    />
                    <img
                        src={assets.photo2}
                        alt="Home Design Evening atmosphere"
                        className="h-[14rem] w-full object-cover object-[42%_center]"
                    />
                </div>
                <ScrollReveal animation="fade-up" duration={400} delay={150}>
                    <p className="mt-[1.375rem] pb-[1.75rem] font-sans text-[0.9375rem] leading-[1.4] font-normal tracking-[-0.026em] text-[#141414]">
                        Home Design Evening brings the entire ArchiPro ecosystem
                        to life in one room. Meet the brands, speak with the
                        professionals, hear from the experts and leave with a
                        clear next step for your project.
                    </p>
                </ScrollReveal>
                {showRegisterButton && (
                    <ScrollReveal
                        animation="fade-up"
                        duration={350}
                        delay={120}
                    >
                        <button
                            type="button"
                            onClick={onRegister}
                            className={`mt-6 flex h-[3.25rem] w-full items-center justify-center rounded-full border border-[#141414] bg-[#141414] ${showRegisterButton ? '' : 'hidden'}`}
                        >
                            <span className="text-[0.9375rem] font-medium text-white">
                                Register Now — Free
                            </span>
                        </button>
                    </ScrollReveal>
                )}
            </section>
        );
    }

    return (
        <section
            data-testid="the-evening"
            className="relative w-full bg-white pt-[6.725rem]"
        >
            <HDEDesktopFrame className="px-[3.125rem]">
                {/* Section title */}
                <ScrollReveal animation="fade-left" duration={500}>
                    <p className="font-sans text-[2.856rem] leading-[0.9] font-medium text-black uppercase">
                        The Evening
                    </p>
                </ScrollReveal>

                {/* Content row: two images + description */}
                <div className="mt-[3.125rem] flex items-center gap-[3.125rem]">
                    {/* Photo pair */}
                    <div className="flex shrink-0 gap-4">
                        <ScrollReveal animation="scale-settle" duration={900}>
                            <img
                                src={assets.photo1}
                                alt="Home Design Evening event"
                                className="overflow-hidden object-cover"
                                style={{
                                    width: '27.15rem',
                                    height: '34.25rem',
                                    backgroundColor: '#c4c4c4',
                                }}
                            />
                        </ScrollReveal>
                        <ScrollReveal
                            animation="scale-settle"
                            duration={900}
                            delay={150}
                        >
                            <img
                                src={assets.photo2}
                                alt="Home Design Evening atmosphere"
                                className="overflow-hidden object-cover"
                                style={{
                                    width: '27.15rem',
                                    height: '34.25rem',
                                    backgroundColor: '#c4c4c4',
                                }}
                            />
                        </ScrollReveal>
                    </div>

                    {/* Description */}
                    <ScrollReveal
                        animation="fade-up"
                        duration={600}
                        delay={250}
                    >
                        <p className="w-[45.12rem] font-sans text-[1.75rem] leading-[125%] font-medium tracking-[-0.035rem] text-black">
                            Home Design Evening brings the entire ArchiPro
                            ecosystem to life in one room. Meet the brands,
                            speak with the professionals, hear from the experts
                            and leave with a clear next step for your project.
                        </p>
                    </ScrollReveal>
                </div>
            </HDEDesktopFrame>
        </section>
    );
};
